/**
 * The emoji a reaction can be picked from (owner, 2026-10-17: Slack 這種使用 emoji 來回覆某一個信息).
 *
 * A curated set, not the whole Unicode list: the picker is for answering a message, and a few hundred
 * faces, hands, hearts and symbols cover what people answer with. Bundled as plain data — no emoji
 * library, no image sprites: the system's own emoji font draws them, the way the rest of the app shows
 * emoji in messages. Each entry carries English words to search by (the app's UI language).
 *
 * The cloud accepts ANY single emoji (chat/lambda/reactions.ts isReactionEmoji), so a reaction someone
 * added from another client still shows here even if this list does not have it.
 */
export type EmojiEntry = readonly [emoji: string, words: string]

/** The row shown first — what people answer with most. */
export const QUICK: readonly string[] = ['👍', '❤️', '😂', '🎉', '😮', '😢', '🙏', '👀', '✅', '🔥']

export const CATEGORIES: ReadonlyArray<{ name: string; emoji: readonly EmojiEntry[] }> = [
  {
    name: 'Smileys',
    emoji: [
      ['😀', 'grin smile happy'], ['😃', 'smile happy joy'], ['😄', 'smile laugh happy'], ['😁', 'grin beam'],
      ['😆', 'laugh squint'], ['😅', 'sweat smile relief'], ['🤣', 'rofl rolling laugh'], ['😂', 'joy tears laugh'],
      ['🙂', 'slight smile'], ['🙃', 'upside down'], ['😉', 'wink'], ['😊', 'blush smile'], ['😇', 'halo angel innocent'],
      ['🥰', 'love hearts adore'], ['😍', 'heart eyes love'], ['🤩', 'star struck wow'], ['😘', 'kiss'],
      ['😋', 'yum tasty'], ['😛', 'tongue'], ['😜', 'wink tongue crazy'], ['🤪', 'zany crazy'], ['🤑', 'money'],
      ['🤗', 'hug'], ['🤭', 'oops giggle'], ['🤫', 'shush quiet'], ['🤔', 'thinking hmm'], ['🤐', 'zipper mouth'],
      ['🤨', 'raised eyebrow skeptical'], ['😐', 'neutral'], ['😑', 'expressionless'], ['😶', 'no mouth'],
      ['😏', 'smirk'], ['😒', 'unamused'], ['🙄', 'eye roll'], ['😬', 'grimace awkward'], ['😌', 'relieved'],
      ['😔', 'pensive sad'], ['😪', 'sleepy'], ['😴', 'sleeping zzz'], ['😷', 'mask sick'], ['🤒', 'thermometer sick'],
      ['🤯', 'mind blown exploding'], ['🥳', 'party celebrate'], ['😎', 'cool sunglasses'], ['🤓', 'nerd'],
      ['🧐', 'monocle inspect'], ['😕', 'confused'], ['😟', 'worried'], ['🙁', 'frown'], ['😮', 'open mouth wow surprised'],
      ['😯', 'hushed'], ['😲', 'astonished'], ['😳', 'flushed embarrassed'], ['🥺', 'pleading puppy eyes'],
      ['😢', 'cry sad tear'], ['😭', 'sob crying'], ['😱', 'scream fear'], ['😤', 'triumph huff'], ['😡', 'angry rage'],
      ['🤬', 'cursing'], ['💀', 'skull dead'], ['🤡', 'clown'], ['👻', 'ghost'], ['🤖', 'robot bot'], ['💩', 'poop'],
      ['🫠', 'melting'], ['🫡', 'salute'], ['🥲', 'smile tear'],
    ],
  },
  {
    name: 'Hands',
    emoji: [
      ['👍', 'thumbs up yes like +1 approve'], ['👎', 'thumbs down no dislike -1'], ['👌', 'ok perfect'],
      ['✌️', 'victory peace'], ['🤞', 'fingers crossed luck'], ['🤟', 'love you'], ['🤘', 'rock on'], ['🤙', 'call me'],
      ['👈', 'point left'], ['👉', 'point right'], ['👆', 'point up'], ['👇', 'point down'], ['☝️', 'index up'],
      ['✋', 'raised hand stop'], ['👋', 'wave hello bye'], ['👏', 'clap applause'], ['🙌', 'raised hands hooray'],
      ['🫶', 'heart hands'], ['👐', 'open hands'], ['🤲', 'palms up'], ['🤝', 'handshake deal'], ['🙏', 'pray thanks please'],
      ['✍️', 'writing'], ['💪', 'muscle strong'], ['🫵', 'you point'], ['👀', 'eyes looking watching'], ['🧠', 'brain smart'],
    ],
  },
  {
    name: 'Hearts',
    emoji: [
      ['❤️', 'heart love red'], ['🧡', 'orange heart'], ['💛', 'yellow heart'], ['💚', 'green heart'], ['💙', 'blue heart'],
      ['💜', 'purple heart'], ['🖤', 'black heart'], ['🤍', 'white heart'], ['🤎', 'brown heart'], ['💔', 'broken heart'],
      ['❤️‍🔥', 'heart on fire'], ['💕', 'two hearts'], ['💖', 'sparkling heart'], ['💯', 'hundred 100 perfect'],
      ['💢', 'anger'], ['💥', 'boom collision'], ['💫', 'dizzy'], ['💦', 'sweat drops'], ['💤', 'zzz sleep'],
    ],
  },
  {
    name: 'Celebration',
    emoji: [
      ['🎉', 'tada party celebrate hooray'], ['🎊', 'confetti'], ['🥂', 'cheers toast'], ['🍾', 'champagne'],
      ['🎂', 'birthday cake'], ['🎁', 'gift present'], ['🏆', 'trophy win'], ['🥇', 'gold medal first'], ['🏅', 'medal'],
      ['⭐', 'star'], ['🌟', 'glowing star'], ['✨', 'sparkles'], ['🔥', 'fire hot lit'], ['🚀', 'rocket launch ship'],
      ['🎯', 'target bullseye'], ['💡', 'idea bulb'], ['⚡', 'lightning zap fast'], ['🌈', 'rainbow'],
    ],
  },
  {
    name: 'Symbols',
    emoji: [
      ['✅', 'check done yes complete'], ['☑️', 'ballot check'], ['✔️', 'check mark'], ['❌', 'x cross no wrong'],
      ['❎', 'cross mark button'], ['❗', 'exclamation important'], ['❓', 'question'], ['⁉️', 'interrobang'],
      ['‼️', 'double exclamation'], ['⚠️', 'warning caution'], ['🚫', 'prohibited no'], ['⛔', 'no entry stop'],
      ['🆗', 'ok button'], ['🆕', 'new'], ['🆒', 'cool button'], ['🔴', 'red circle'], ['🟡', 'yellow circle'],
      ['🟢', 'green circle'], ['🔵', 'blue circle'], ['⏳', 'hourglass waiting'], ['⌛', 'hourglass done'],
      ['⏰', 'alarm clock'], ['🔔', 'bell'], ['🔕', 'bell off mute'], ['📌', 'pin'], ['📎', 'paperclip'], ['🔗', 'link'],
      ['🔒', 'lock'], ['🔓', 'unlock'], ['🔑', 'key'], ['➕', 'plus'], ['➖', 'minus'], ['➡️', 'right arrow'],
      ['⬅️', 'left arrow'], ['⬆️', 'up arrow'], ['⬇️', 'down arrow'], ['🔁', 'repeat'], ['🔄', 'refresh reload'],
      ['1️⃣', 'one 1'], ['2️⃣', 'two 2'], ['3️⃣', 'three 3'], ['#️⃣', 'hash number'],
    ],
  },
  {
    name: 'Work',
    emoji: [
      ['💻', 'laptop computer'], ['🖥️', 'desktop computer'], ['⌨️', 'keyboard'], ['🖱️', 'mouse'], ['📱', 'phone mobile'],
      ['🐛', 'bug'], ['🛠️', 'tools fix'], ['🔧', 'wrench'], ['🔨', 'hammer'], ['⚙️', 'gear settings'], ['🧪', 'test tube experiment'],
      ['📦', 'package box ship'], ['📝', 'memo note write'], ['📄', 'document page'], ['📊', 'chart bar'], ['📈', 'chart up'],
      ['📉', 'chart down'], ['🗂️', 'dividers files'], ['📁', 'folder'], ['📅', 'calendar date'], ['🔍', 'search magnify'],
      ['📣', 'megaphone announce'], ['💬', 'speech chat'], ['🗣️', 'speaking'], ['📞', 'phone call'], ['✉️', 'envelope mail'],
      ['🚢', 'ship it'], ['🏗️', 'construction build'], ['🚧', 'construction wip'], ['🧹', 'broom cleanup'], ['☕', 'coffee break'],
    ],
  },
  {
    name: 'Nature',
    emoji: [
      ['🐶', 'dog'], ['🐱', 'cat'], ['🐭', 'mouse'], ['🦊', 'fox'], ['🐻', 'bear'], ['🐼', 'panda'], ['🐨', 'koala'],
      ['🐯', 'tiger'], ['🦁', 'lion'], ['🐸', 'frog'], ['🐵', 'monkey'], ['🙈', 'see no evil'], ['🙉', 'hear no evil'],
      ['🙊', 'speak no evil'], ['🐔', 'chicken'], ['🐧', 'penguin'], ['🦄', 'unicorn'], ['🐝', 'bee'], ['🦋', 'butterfly'],
      ['🐢', 'turtle slow'], ['🐍', 'snake python'], ['🐙', 'octopus'], ['🦀', 'crab rust'], ['🐳', 'whale docker'],
      ['🌱', 'seedling'], ['🌲', 'tree'], ['🌵', 'cactus'], ['🍀', 'clover luck'], ['🌸', 'blossom'], ['🌻', 'sunflower'],
      ['🌙', 'moon night'], ['☀️', 'sun'], ['⛅', 'cloudy'], ['🌧️', 'rain'], ['❄️', 'snow cold'], ['🌊', 'wave ocean'],
    ],
  },
  {
    name: 'Food',
    emoji: [
      ['🍎', 'apple'], ['🍌', 'banana'], ['🍓', 'strawberry'], ['🍉', 'watermelon'], ['🍕', 'pizza'], ['🍔', 'burger'],
      ['🍟', 'fries'], ['🌮', 'taco'], ['🍣', 'sushi'], ['🍜', 'ramen noodles'], ['🍚', 'rice'], ['🥟', 'dumpling'],
      ['🍩', 'donut'], ['🍪', 'cookie'], ['🍰', 'cake'], ['🍿', 'popcorn'], ['🧋', 'bubble tea boba'], ['🍵', 'tea'],
      ['🍺', 'beer'], ['🍷', 'wine'],
    ],
  },
]

const ALL: readonly EmojiEntry[] = CATEGORIES.flatMap((c) => c.emoji)

/** Emoji whose words start with every typed word (or that ARE what was typed). Empty query → nothing. */
export function searchEmoji(query: string, limit = 40): string[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const parts = q.split(/\s+/)
  const out: string[] = []
  for (const [e, words] of ALL) {
    const ws = words.split(' ')
    if (e === q || parts.every((p) => ws.some((w) => w.startsWith(p)))) {
      if (!out.includes(e)) out.push(e)
      if (out.length >= limit) break
    }
  }
  return out
}
